import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";

const WEEK_DAYS = [
  { value: "Mon", label: "Mon" },
  { value: "Tue", label: "Tue" },
  { value: "Wed", label: "Wed" },
  { value: "Thu", label: "Thu" },
  { value: "Fri", label: "Fri" },
  { value: "Sat", label: "Sat" },
  { value: "Sun", label: "Sun" },
];

const EMPTY_FORM = {
  pay_frequency: "",
  payroll_cycle_name: "",
  monthly_period_start_day: "",
  monthly_period_start_day_2: "",
  week_start_day: "",
  week_start_day_2: "",
};

const clearScheduleFields = (frequency) => {
  const cleared = {
    monthly_period_start_day: "",
    monthly_period_start_day_2: "",
    week_start_day: "",
    week_start_day_2: "",
  };

  switch (frequency) {
    case "Monthly":
      delete cleared.monthly_period_start_day;
      break;
    case "SemiMonthly":
      delete cleared.monthly_period_start_day;
      delete cleared.monthly_period_start_day_2;
      break;
    case "Weekly":
      delete cleared.week_start_day;
      break;
    case "BiWeekly":
      delete cleared.week_start_day;
      delete cleared.week_start_day_2;
      break;
    default:
      break;
  }

  return cleared;
};

const buildPayload = (data, orgId, cycleId = null) => {
  const payload = {
    organization_id: orgId,
    payroll_cycle_name: data.payroll_cycle_name,
    pay_frequency: data.pay_frequency,
    monthly_period_start_day: null,
    monthly_period_start_day_2: null,
    week_start_day: null,
    week_start_day_2: null,
  };

  if (cycleId) {
    payload.organization_payroll_cycle_id = cycleId;
  }

  switch (data.pay_frequency) {
    case "Monthly":
      payload.monthly_period_start_day = Number(data.monthly_period_start_day);
      break;
    case "SemiMonthly":
      payload.monthly_period_start_day = Number(data.monthly_period_start_day);
      payload.monthly_period_start_day_2 = Number(data.monthly_period_start_day_2);
      break;
    case "Weekly":
      payload.week_start_day = data.week_start_day;
      break;
    case "BiWeekly":
      payload.week_start_day = data.week_start_day;
      payload.week_start_day_2 = data.week_start_day_2;
      break;
    default:
      break;
  }

  return payload;
};

function PayrollCycleForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit");
  const [btnLoading, setbtnLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getdataById = async () => {
      const response = await axios.get(
        `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-cycle/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const record = response?.data?.payroll || {};
      setFormData({
        pay_frequency: record.pay_frequency || "",
        payroll_cycle_name: record.payroll_cycle_name || "",
        monthly_period_start_day: record.monthly_period_start_day ?? "",
        monthly_period_start_day_2: record.monthly_period_start_day_2 ?? "",
        week_start_day: record.week_start_day || "",
        week_start_day_2: record.week_start_day_2 || "",
      });
      setLoading(false);
    };

    if ((mode === "edit" || mode === "view") && id) {
      setLoading(true);
      getdataById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "pay_frequency") {
      setFormData((prev) => ({
        ...prev,
        pay_frequency: value,
        ...clearScheduleFields(value),
      }));
      setFormErrors((prev) => ({
        ...prev,
        pay_frequency: "",
        monthly_period_start_day: "",
        monthly_period_start_day_2: "",
        week_start_day: "",
        week_start_day_2: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDayChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*$/.test(value)) {
      if (value === "" || Number(value) <= 31) {
        handleChange(e);
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.payroll_cycle_name?.trim()) {
      errors.payroll_cycle_name = "Payroll cycle name is required.";
    }

    if (!formData.pay_frequency) {
      errors.pay_frequency = "Pay frequency is required.";
    }

    switch (formData.pay_frequency) {
      case "Monthly":
        if (!formData.monthly_period_start_day) {
          errors.monthly_period_start_day = "Monthly start day is required.";
        } else if (Number(formData.monthly_period_start_day) < 1 || Number(formData.monthly_period_start_day) > 31) {
          errors.monthly_period_start_day = "Enter a day between 1 and 31.";
        }
        break;
      case "SemiMonthly":
        if (!formData.monthly_period_start_day) {
          errors.monthly_period_start_day = "First monthly start day is required.";
        }
        if (!formData.monthly_period_start_day_2) {
          errors.monthly_period_start_day_2 = "Second monthly start day is required.";
        }
        if (
          formData.monthly_period_start_day &&
          formData.monthly_period_start_day_2 &&
          formData.monthly_period_start_day === formData.monthly_period_start_day_2
        ) {
          errors.monthly_period_start_day_2 = "Second start day must be different.";
        }
        break;
      case "Weekly":
        if (!formData.week_start_day) {
          errors.week_start_day = "Week start day is required.";
        }
        break;
      case "BiWeekly":
        if (!formData.week_start_day) {
          errors.week_start_day = "First week start day is required.";
        }
        if (!formData.week_start_day_2) {
          errors.week_start_day_2 = "Second week start day is required.";
        }
        if (
          formData.week_start_day &&
          formData.week_start_day_2 &&
          formData.week_start_day === formData.week_start_day_2
        ) {
          errors.week_start_day_2 = "Second week start day must be different.";
        }
        break;
      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setbtnLoading(true);

    const payload = buildPayload(formData, org.organization_id, mode === "edit" ? id : null);

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-cycle/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-cycle`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      toast.success(
        mode === "edit" ? "Payroll Cycle updated!" : "Payroll Cycle created!"
      );
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setbtnLoading(false);
    }
  };

  const renderScheduleFields = () => {
    switch (formData.pay_frequency) {
      case "Monthly":
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Monthly start day"
              name="monthly_period_start_day"
              type="number"
              value={formData.monthly_period_start_day}
              onChange={handleDayChange}
              error={!!formErrors.monthly_period_start_day}
              helperText={formErrors.monthly_period_start_day}
              required
              inputProps={{ min: 1, max: 31 }}
              disabled={mode === "view"}
            />
          </Grid>
        );
      case "SemiMonthly":
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="First monthly start day"
                name="monthly_period_start_day"
                type="number"
                value={formData.monthly_period_start_day}
                onChange={handleDayChange}
                error={!!formErrors.monthly_period_start_day}
                helperText={formErrors.monthly_period_start_day}
                required
                inputProps={{ min: 1, max: 31 }}
                disabled={mode === "view"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Second monthly start day"
                name="monthly_period_start_day_2"
                type="number"
                value={formData.monthly_period_start_day_2}
                onChange={handleDayChange}
                error={!!formErrors.monthly_period_start_day_2}
                helperText={formErrors.monthly_period_start_day_2}
                required
                inputProps={{ min: 1, max: 31 }}
                disabled={mode === "view"}
              />
            </Grid>
          </>
        );
      case "Weekly":
        return (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Week start day"
              name="week_start_day"
              value={formData.week_start_day}
              onChange={handleChange}
              error={!!formErrors.week_start_day}
              helperText={formErrors.week_start_day}
              required
              disabled={mode === "view"}
            >
              {WEEK_DAYS.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
      case "BiWeekly":
        return (
          <>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="First week start day"
                name="week_start_day"
                value={formData.week_start_day}
                onChange={handleChange}
                error={!!formErrors.week_start_day}
                helperText={formErrors.week_start_day}
                required
                disabled={mode === "view"}
              >
                {WEEK_DAYS.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Second week start day"
                name="week_start_day_2"
                value={formData.week_start_day_2}
                onChange={handleChange}
                error={!!formErrors.week_start_day_2}
                helperText={formErrors.week_start_day_2}
                required
                disabled={mode === "view"}
              >
                {WEEK_DAYS.map((day) => (
                  <MenuItem key={`second-${day.value}`} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Payroll Cycles"}
      addMessage={"Payroll Cycles"}
      homeLink={"/payroll/cycles"}
      homeText={"Payroll Cycles"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payroll cycle Name"
                    name="payroll_cycle_name"
                    value={formData.payroll_cycle_name}
                    onChange={handleChange}
                    error={!!formErrors.payroll_cycle_name}
                    helperText={formErrors.payroll_cycle_name}
                    inputProps={{ maxLength: 100 }}
                    required
                    disabled={mode === "view"}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Pay Frequency"
                    name="pay_frequency"
                    value={formData.pay_frequency}
                    onChange={handleChange}
                    error={!!formErrors.pay_frequency}
                    helperText={formErrors.pay_frequency}
                    required
                    disabled={mode === "view"}
                  >
                    <MenuItem value="Monthly">Monthly</MenuItem>
                    <MenuItem value="SemiMonthly">SemiMonthly</MenuItem>
                    <MenuItem value="BiWeekly">BiWeekly</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Daily">Daily</MenuItem>
                  </TextField>
                </Grid>

                {renderScheduleFields()}

                {mode !== "view" && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleSubmit}
                      disabled={loading || btnLoading}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    >
                      {btnLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Grid>
                )}

                {mode === "edit" && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#115293" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default PayrollCycleForm;
